import { useTranslation } from "@/core/i18n";
import { lastDayBeforeExpiry } from "@/modules/giftCards/PromotionalExpiry/promotionalExpiry";
import { PromotionalExpiryLine } from "@/modules/giftCards/PromotionalExpiry/PromotionalExpiryLine";
import { usePracticeTimeZone } from "@/modules/giftCards/PromotionalExpiry/usePracticeTimeZone";

import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import RepeatCash from "atoms/icon/icons/RepeatCash.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { AnimatedBackground } from "components/AnimatedBackground/AnimatedBackground";
import { formatCurrency } from "toolbox/Money";

interface WalletRepeatCashCardProps {
  amount: number;
  /** The balance's open promotional grants, soonest expiry first; each gets its own line. */
  expiringGrants: ReadonlyArray<Models<"DisplayExpiringRepeatCashGrant">>;
  onClick: () => void;
}

export const WalletRepeatCashCard = (props: WalletRepeatCashCardProps) => {
  const { t } = useTranslation();
  const practiceTimeZone = usePracticeTimeZone(props.expiringGrants.length > 0);
  return (
    <div className="relative flex h-[200px] w-full max-w-md flex-col gap-4 overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 via-black to-gray-800">
      {/* Animated background */}
      <AnimatedBackground
        mode="black"
        durationSeconds={10}
        className="absolute inset-0 h-full w-full opacity-40"
      />

      {/* Subtle card pattern/texture */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Decorative plus signs */}
      <div className="absolute right-6 top-4 text-3xl font-light text-white opacity-10">+</div>
      <div className="absolute left-6 top-8 text-2xl font-light text-white opacity-10">+</div>
      <div className="absolute bottom-8 right-10 text-2xl font-light text-white opacity-10">+</div>

      {/* RepeatCash icon */}
      <div className="absolute left-4 top-3">
        <Icon svg={RepeatCash} size="medium" color="white" />
      </div>

      {/* Balance positioned next to icon */}
      <div className="absolute left-14 top-3 text-h2 text-white">
        {formatCurrency(props.amount)}
      </div>

      {props.expiringGrants.length > 0 && practiceTimeZone !== undefined && (
        <div className="absolute left-4 right-4 top-16 flex flex-col gap-1">
          {props.expiringGrants.map((grant) => (
            <PromotionalExpiryLine
              key={grant.grantTransactionId}
              kind="balance"
              amountInCents={grant.remainingInCents}
              expiryDay={lastDayBeforeExpiry(grant.expiresOnUtc, practiceTimeZone)}
              className="text-white"
            />
          ))}
        </div>
      )}
      <TextButton
        className="absolute bottom-1 right-2 text-white"
        endIconSVG={ChevronRight}
        onClick={props.onClick}
      >
        {t("repeatCashCard.shopServices")}
      </TextButton>
    </div>
  );
};
